import * as React from 'react';
import { Link } from 'react-router-dom';
import { motion } from 'framer-motion';
import {
  ArrowRight,
  Check,
  Sparkles,
  FileText,
  Receipt,
  Users,
  Banknote,
  Briefcase,
  Activity,
  CreditCard,
  Mail,
  ChevronRight,
  Wallet,
  AlertCircle,
  CheckCircle2,
  UserCheck,
  Mailbox,
  Calendar,
  FileDown,
  FolderKanban,
  Play,
  PenTool,
} from 'lucide-react';
import { Eyebrow } from '../../design-system/pds';
import { MarketingPageHead } from '../MarketingPageHead';
import { Parallax, CountUp } from '../components/motion';

/**
 * FeaturesPage
 * --------------------------------------------------------------
 * Deep feature tour for Worqly. One anchor section per module —
 * the landing page deep-links into these (#invoicing, #expenses,
 * etc.) and the marketing nav surfaces this page.
 *
 * Visual language matches LandingPage.tsx / PricingPage.tsx:
 *   - coral accent, pearl canvas
 *   - font-light display headlines + italic-serif emphasis
 *   - rounded-3xl translucent cards
 *   - dual light/dark via `dark:` variants
 *   - scroll-triggered FADE_UP + STAGGER motion
 */

const FADE_UP = {
  hidden: { opacity: 0, y: 20 },
  visible: { opacity: 1, y: 0, transition: { duration: 0.6, ease: [0.16, 1, 0.3, 1] as any } },
};

const STAGGER = {
  hidden: { opacity: 0 },
  visible: { opacity: 1, transition: { staggerChildren: 0.08 } },
};

/* ─────────────────────────────────────────────────────────────
 * Module catalogue
 * ───────────────────────────────────────────────────────────── */

type Module = {
  id: string;
  eyebrow: string;
  title: React.ReactNode;
  body: string[];
  bullets: string[];
  Icon: React.ComponentType<{ className?: string }>;
  Mock: React.FC;
};

export function FeaturesPage() {
  return (
    <div className="overflow-x-hidden">
      <MarketingPageHead
        title="Features"
        description="Nine modules, one workspace. Invoicing, HR, payroll, projects, monitoring, expenses, e-sign, payments and email — all wired together."
      />
      <Hero />
      <ModulesNav />
      {MODULES.map((m, i) => (
        <ModuleSection key={m.id} module={m} index={i} />
      ))}
      <FooterCTA />
    </div>
  );
}

/* ─────────────────────────────────────────────────────────────
 * 1. HERO
 * ───────────────────────────────────────────────────────────── */

function Hero() {
  return (
    <section className="relative pt-12 pb-10 lg:pt-20 lg:pb-14">
      <div className="max-w-4xl mx-auto px-6 lg:px-8 text-center">
        <motion.div initial="hidden" animate="visible" variants={STAGGER}>
          <motion.div variants={FADE_UP}>
            <span
              className="inline-flex items-center gap-2 px-3 py-1.5 rounded-full text-[11px] font-bold uppercase tracking-[0.10em]
                bg-white/80 text-ink shadow-pill ring-1 ring-ink/5
                dark:bg-white/10 dark:text-white dark:ring-white/10 dark:shadow-none"
            >
              <Sparkles className="h-3 w-3 text-coral" />
              Nine modules. One workspace.
            </span>
          </motion.div>

          <motion.h1
            variants={FADE_UP}
            className="mt-6 font-light text-display-xl text-ink dark:text-white tracking-[-0.035em] leading-[1.02] text-balance"
          >
            One system.{' '}
            <span className="italic font-serif text-coral">Every operation.</span>
          </motion.h1>

          <motion.p
            variants={FADE_UP}
            className="mt-6 text-body-lg text-ink/65 dark:text-white/65 max-w-2xl mx-auto text-pretty"
          >
            Invoicing, expenses, e-sign, HR, payroll, projects, time, payments, email — built from the
            ground up to share data, share login, and share one bill.
          </motion.p>

          <motion.div
            variants={FADE_UP}
            className="mt-8 flex flex-wrap gap-3 justify-center"
          >
            <Link
              to="/signup"
              className="inline-flex items-center justify-center gap-2 h-12 px-7 rounded-full bg-coral text-white text-[14.5px] font-semibold shadow-cta hover:bg-coral-600 transition-colors focus-coral"
            >
              Start free
              <ArrowRight className="h-4 w-4" />
            </Link>
            <Link
              to="/pricing"
              className="inline-flex items-center justify-center gap-2 h-12 px-7 rounded-full bg-white text-ink text-[14.5px] font-semibold shadow-pill ring-1 ring-ink/10 hover:bg-pearl-50 dark:bg-white/10 dark:text-white dark:ring-white/10 dark:hover:bg-white/15 transition-colors focus-coral"
            >
              See pricing
            </Link>
          </motion.div>

          <motion.p
            variants={FADE_UP}
            className="mt-5 text-[12.5px] text-ink/55 dark:text-white/55 flex flex-wrap items-center gap-x-2 gap-y-1 justify-center"
          >
            <span>No card to start</span>
            <span className="text-ink/25 dark:text-white/25">·</span>
            <span>Turn modules on / off any time</span>
            <span className="text-ink/25 dark:text-white/25">·</span>
            <span>Cancel any time</span>
          </motion.p>
        </motion.div>
      </div>
    </section>
  );
}

/* ─────────────────────────────────────────────────────────────
 * 2. MODULES NAV — quick jump strip
 * ───────────────────────────────────────────────────────────── */

function ModulesNav() {
  return (
    <section className="py-8 lg:py-10 border-y border-ink/8 dark:border-white/10 bg-white/40 dark:bg-white/[0.02] backdrop-blur-md">
      <div className="max-w-site mx-auto px-6 lg:px-8">
        <p className="text-center text-[11px] font-bold uppercase tracking-[0.18em] text-ink/55 dark:text-white/55 mb-5">
          Jump to a module
        </p>
        <ul className="flex flex-wrap items-center justify-center gap-2">
          {MODULES.map(({ id, eyebrow, Icon }) => (
            <li key={id}>
              <a
                href={`#${id}`}
                className="inline-flex items-center gap-1.5 px-3 py-1.5 rounded-full bg-white/70 text-ink/75 ring-1 ring-ink/5 shadow-pill hover:text-ink hover:bg-white dark:bg-white/10 dark:text-white/70 dark:ring-white/10 dark:hover:text-white dark:hover:bg-white/15 transition-colors text-[12.5px] font-semibold"
              >
                <Icon className="h-3.5 w-3.5 text-coral" />
                {eyebrow}
              </a>
            </li>
          ))}
        </ul>
      </div>
    </section>
  );
}

/* ─────────────────────────────────────────────────────────────
 * 3. MODULE SECTION (repeated)
 * ───────────────────────────────────────────────────────────── */

function ModuleSection({ module, index }: { module: Module; index: number }) {
  const flip = index % 2 === 1;
  const tinted = index % 2 === 0;

  // Monitoring module also serves as the target for the landing page's
  // "Time tracking" card (#time-tracking) — they're the same module.
  const aliasId = module.id === 'monitoring' ? 'time-tracking' : null;

  return (
    <section
      id={module.id}
      className={
        'py-20 lg:py-24 scroll-mt-24 ' +
        (tinted
          ? ''
          : 'bg-pearl-50/60 dark:bg-white/[0.02] border-y border-ink/5 dark:border-white/5')
      }
    >
      {aliasId && <span id={aliasId} className="block scroll-mt-24" aria-hidden />}
      <div className="max-w-site mx-auto px-6 lg:px-8">
        <motion.div
          initial="hidden"
          whileInView="visible"
          viewport={{ once: true, margin: '-80px' }}
          variants={STAGGER}
          className={
            'grid grid-cols-1 lg:grid-cols-12 gap-10 lg:gap-14 items-center ' +
            (flip ? 'lg:[&>*:first-child]:order-2' : '')
          }
        >
          {/* Copy */}
          <div className="lg:col-span-6">
            <motion.div variants={FADE_UP} className="flex items-center gap-3">
              <span className="inline-flex h-10 w-10 items-center justify-center rounded-2xl bg-coral/10 text-coral-600 dark:bg-coral/15 dark:text-coral-400">
                <module.Icon className="h-5 w-5" />
              </span>
              <Eyebrow className="!text-coral">{module.eyebrow}</Eyebrow>
            </motion.div>

            <motion.h2
              variants={FADE_UP}
              className="mt-4 font-light text-display-md text-ink dark:text-white tracking-[-0.025em] text-balance"
            >
              {module.title}
            </motion.h2>

            {module.body.map((para, i) => (
              <motion.p
                key={i}
                variants={FADE_UP}
                className="mt-4 text-[15px] text-ink/65 dark:text-white/65 leading-relaxed max-w-xl"
              >
                {para}
              </motion.p>
            ))}

            <motion.ul variants={FADE_UP} className="mt-6 grid grid-cols-1 sm:grid-cols-2 gap-2.5">
              {module.bullets.map((b) => (
                <li key={b} className="flex items-start gap-2.5">
                  <span className="mt-0.5 inline-flex h-5 w-5 items-center justify-center rounded-full bg-coral/12 text-coral-700 dark:bg-coral/20 dark:text-coral-300 shrink-0">
                    <Check className="h-3 w-3" strokeWidth={3} />
                  </span>
                  <span className="text-[13.5px] text-ink/75 dark:text-white/75 leading-snug">
                    {b}
                  </span>
                </li>
              ))}
            </motion.ul>

            <motion.div variants={FADE_UP} className="mt-7 flex flex-wrap gap-3">
              <Link
                to="/signup"
                className="inline-flex items-center justify-center gap-2 h-11 px-5 rounded-full bg-coral text-white text-[13.5px] font-semibold shadow-cta hover:bg-coral-600 transition-colors focus-coral"
              >
                Start free
                <ArrowRight className="h-4 w-4" />
              </Link>
              <Link
                to="/pricing"
                className="inline-flex items-center gap-1 text-[13.5px] font-semibold text-ink/70 hover:text-coral dark:text-white/70 dark:hover:text-coral-400 transition-colors px-2 h-11"
              >
                See pricing
                <ChevronRight className="h-4 w-4" />
              </Link>
            </motion.div>
          </div>

          {/* Mock */}
          <motion.div variants={FADE_UP} className="lg:col-span-6">
            <module.Mock />
          </motion.div>
        </motion.div>
      </div>
    </section>
  );
}

/* ─────────────────────────────────────────────────────────────
 * Mocks — each is a styled, honest marketing illustration of
 * the module. Built with the same primitives the real product
 * uses. Not a fake screenshot — clearly an illustrative card.
 * ───────────────────────────────────────────────────────────── */

/** Design width the mock internals are laid out for. */
const MOCK_CANVAS_W = 520;

/**
 * MockCanvas — renders its children at a fixed MOCK_CANVAS_W and scales them
 * down to fit the available width, so the dense mock layouts never reflow.
 *
 * Only ever scales DOWN (never up), so on desktop the mock renders at its
 * natural size and stays pixel-crisp. Height is tracked with a ResizeObserver
 * and multiplied by the same factor, otherwise the transform would leave a gap
 * below the mock (transforms don't affect layout height).
 */
function MockCanvas({ children }: { children: React.ReactNode }) {
  const outerRef = React.useRef<HTMLDivElement | null>(null);
  const innerRef = React.useRef<HTMLDivElement | null>(null);
  const [scale, setScale] = React.useState(1);
  const [innerH, setInnerH] = React.useState<number | null>(null);

  React.useEffect(() => {
    const outer = outerRef.current, inner = innerRef.current;
    if (!outer || !inner) return;
    const measure = () => {
      const avail = outer.clientWidth;
      if (avail > 0) setScale(Math.min(1, avail / MOCK_CANVAS_W));
      setInnerH(inner.scrollHeight);
    };
    measure();
    const ro = new ResizeObserver(measure);
    ro.observe(outer);
    ro.observe(inner);
    return () => ro.disconnect();
  }, []);

  return (
    <div ref={outerRef} style={{ height: innerH != null ? innerH * scale : undefined }}>
      <div
        ref={innerRef}
        style={{ width: MOCK_CANVAS_W, transform: `scale(${scale})`, transformOrigin: 'top left' }}
      >
        {children}
      </div>
    </div>
  );
}

function MockShell({
  title,
  children,
  paid,
}: {
  title: string;
  children: React.ReactNode;
  paid?: string;
}) {
  return (
    <Parallax distance={44} className="relative">
      <div aria-hidden className="absolute -inset-8 bg-coral/15 blur-3xl rounded-full -z-10 animate-mesh-drift motion-ambient" />
      <div className="hover-lift relative rounded-[28px] bg-white/85 dark:bg-white/[0.06] backdrop-blur-xl ring-1 ring-white/60 dark:ring-white/10 shadow-card-lg hover:shadow-[0_28px_70px_-24px_rgba(26,26,26,0.35)] overflow-hidden p-5">
        <div className="flex items-center gap-2 mb-4">
          <span className="size-2.5 rounded-full bg-rose-400 shrink-0" />
          <span className="size-2.5 rounded-full bg-amber-400 shrink-0" />
          <span className="size-2.5 rounded-full bg-lime-neon shrink-0" />
          <span className="ml-3 text-[11px] font-mono text-ink/45 dark:text-white/45 truncate">
            {title}
          </span>
        </div>
        {/* These mocks are dense product illustrations laid out for a ~520px
         *  canvas. Below that, reflowing them makes every internal label fight
         *  for room and burst its card. Scaling the whole thing like an image
         *  instead keeps the composition intact at any width — the aspect ratio
         *  is preserved by scaling the wrapper's height to match. */}
        <MockCanvas>{children}</MockCanvas>
      </div>
      {paid && (
        <div className="absolute -top-3 -right-3 z-10">
          <span className="inline-flex items-center gap-1.5 px-3 py-1.5 bg-lime-neon text-ink rounded-full text-[10.5px] font-bold uppercase tracking-[0.10em] shadow-card">
            {paid}
          </span>
        </div>
      )}
    </Parallax>
  );
}

/* ─────────────────────────────────────────────────────────────
 * Mock primitives — mirror the real dashboard.
 *
 *   `StatusPill` = same className strings used by the real
 *   `StatusChip` (frontend/src/design-system/pds.tsx STATUS_STYLES).
 *   `Avatar` = same hue-rotated pastel as TeamPage's avatarBgFor.
 * ───────────────────────────────────────────────────────────── */

type PillTone = 'paid' | 'sent' | 'overdue' | 'draft' | 'pending' | 'approved' | 'submitted' | 'reimbursed' | 'rejected' | 'active' | 'archived' | 'finalized' | 'success' | 'info';

function pillClass(tone: PillTone): string {
  // Mirrors statusPill() in design-system/visual.ts and STATUS_STYLES in pds.tsx.
  const base = 'inline-flex items-center gap-1.5 rounded-full px-2.5 py-1 text-[10.5px] font-bold uppercase tracking-[0.10em]';
  switch (tone) {
    case 'paid':
    case 'approved':
    case 'reimbursed':
    case 'active':
    case 'success':
    case 'finalized':
      return `${base} bg-lime-neon text-ink ring-1 ring-ink/10`;
    case 'sent':
    case 'pending':
    case 'submitted':
      return `${base} bg-coral/12 text-coral-700 ring-1 ring-coral/30`;
    case 'overdue':
    case 'rejected':
      return `${base} bg-rose-500/10 text-rose-700 ring-1 ring-rose-500/30 dark:bg-rose-500/20 dark:text-rose-200`;
    case 'info':
      return `${base} bg-lavender-100 text-lavender-700 ring-1 ring-lavender-200`;
    case 'archived':
    case 'draft':
    default:
      return `${base} bg-ink/8 text-ink/65 ring-1 ring-ink/10 dark:bg-white/10 dark:text-white/70 dark:ring-white/15`;
  }
}

function StatusPill({ tone, children }: { tone: PillTone; children: React.ReactNode }) {
  return <span className={pillClass(tone)}>{children}</span>;
}

/** Mirrors TeamPage's avatarBgFor — name hash → low-saturation pastel. */
function avatarBg(name: string): string {
  let h = 0;
  for (let i = 0; i < name.length; i++) h = (h * 31 + name.charCodeAt(i)) % 360;
  return `oklch(0.86 0.08 ${h})`;
}
function initials(name: string): string {
  const parts = name.trim().split(/\s+/);
  if (parts.length === 1) return parts[0].slice(0, 2).toUpperCase();
  return (parts[0][0] + parts[parts.length - 1][0]).toUpperCase();
}

/* ─────────────────────────── Invoicing
 *
 * Mirrors frontend/src/modules/invoicing/pages/InvoicesPage.tsx:
 *  - 3-up KPI bento (Total billed wide, Outstanding, Overdue) with
 *    coral/rose icon chips ("h-9 w-9 rounded-full bg-coral/10 …")
 *  - List rows with Avatar + client name + mono invoice_number,
 *    StatusChip + bold tabular money on the right.
 * ───────────────────────────────────────────────────────────── */
const InvoicingMock: React.FC = () => (
  <MockShell title="worqly.io / dashboard / invoices">
    {/* KPI bento — mirrors InvoicesPage 3-up grid */}
    <div className="grid grid-cols-12 gap-3 mb-3">
      <div className="col-span-12 rounded-3xl bg-white/72 dark:bg-white/[0.05] backdrop-blur-xl shadow-card ring-1 ring-ink/[0.08] dark:ring-white/10 p-4">
        <div className="flex items-start justify-between mb-2">
          <Eyebrow className="min-w-0 truncate">Total billed</Eyebrow>
          <div className="h-9 w-9 rounded-full bg-coral/10 flex items-center justify-center shrink-0">
            <FileText className="h-3.5 w-3.5 text-coral-600" />
          </div>
        </div>
        <p className="text-stat-md tabular-nums font-bold text-ink dark:text-white leading-none"><CountUp value="$48,230.00" /></p>
        <p className="text-[12px] text-ink/55 dark:text-white/55 mt-1.5">Across 24 invoices this quarter</p>
      </div>
      <div className="col-span-6 rounded-3xl bg-white/72 dark:bg-white/[0.05] backdrop-blur-xl shadow-card ring-1 ring-ink/[0.08] dark:ring-white/10 p-4">
        <div className="flex items-center justify-between mb-2">
          <Eyebrow className="min-w-0 truncate">Outstanding</Eyebrow>
          <div className="h-9 w-9 rounded-full bg-coral/10 flex items-center justify-center shrink-0">
            <Wallet className="h-3.5 w-3.5 text-coral-600" />
          </div>
        </div>
        <p className="text-[20px] font-bold tabular-nums text-ink dark:text-white leading-none"><CountUp value="$12,400" /></p>
        <p className="text-[11px] text-ink/55 dark:text-white/55 mt-1.5">6 open · 18 paid</p>
      </div>
      <div className="col-span-6 rounded-3xl bg-white/72 dark:bg-white/[0.05] backdrop-blur-xl shadow-card ring-1 ring-ink/[0.08] dark:ring-white/10 p-4">
        <div className="flex items-center justify-between mb-2">
          <Eyebrow className="min-w-0 truncate">Overdue</Eyebrow>
          <div className="h-9 w-9 rounded-full bg-rose-500/15 flex items-center justify-center shrink-0">
            <AlertCircle className="h-3.5 w-3.5 text-rose-600" />
          </div>
        </div>
        <p className="text-[20px] font-bold tabular-nums text-rose-600 leading-none"><CountUp value="$1,820" /></p>
        <p className="text-[11px] text-ink/55 dark:text-white/55 mt-1.5">1 invoice late</p>
      </div>
    </div>
    {/* List card — mirrors InvoicesList <GlassCard padding="p-0"> */}
    <div className="rounded-3xl bg-white/72 dark:bg-white/[0.05] backdrop-blur-xl shadow-card ring-1 ring-ink/[0.08] dark:ring-white/10 overflow-hidden">
      <ul className="divide-y divide-ink/6 dark:divide-white/8">
        {[
          { client: 'Pinegate Co.',     num: 'INV-0042', issued: 'Mar 15', due: 'Mar 29', total: '$2,840.00', status: 'paid' as PillTone },
          { client: 'Salt River Inc.',  num: 'INV-0041', issued: 'Mar 12', due: 'Mar 26', total: '$1,560.00', status: 'sent' as PillTone },
          { client: 'Northwind Studio', num: 'INV-0040', issued: 'Mar 04', due: 'Mar 18', total: '$4,820.00', status: 'overdue' as PillTone },
          { client: 'Rivermark Agency', num: 'INV-0039', issued: 'Mar 01', due: 'Mar 15', total: '$960.00',   status: 'draft' as PillTone },
        ].map((inv) => (
          <li key={inv.num} className="flex items-center gap-3 px-4 py-3 hover:bg-white/40 dark:hover:bg-white/[0.04] transition-colors">
            <span
              aria-hidden
              className="h-9 w-9 rounded-2xl ring-1 ring-ink/5 dark:ring-white/10 flex items-center justify-center text-[11px] font-bold text-ink/85 shrink-0"
              style={{ background: avatarBg(inv.client) }}
            >
              {initials(inv.client)}
            </span>
            <div className="flex-1 min-w-0">
              <div className="flex items-baseline gap-2 flex-wrap">
                <span className="text-[13px] font-semibold text-ink dark:text-white truncate">{inv.client}</span>
                <span className="text-[10.5px] font-mono text-ink/45 dark:text-white/45">{inv.num}</span>
              </div>
              <p className="text-[11px] text-ink/55 dark:text-white/55 mt-0.5">Issued {inv.issued} · Due {inv.due}</p>
            </div>
            <div className="flex flex-col items-end gap-1.5">
              <StatusPill tone={inv.status}>{inv.status}</StatusPill>
              <span className="text-[13px] font-bold tabular-nums text-ink dark:text-white leading-none">{inv.total}</span>
            </div>
          </li>
        ))}
      </ul>
    </div>
  </MockShell>
);

/* ─────────────────────────── Expenses
 *
 * Mirrors frontend/src/modules/expenses/pages/ExpensesPage.tsx:
 *  - KPI bento — "Total in view" wide + Pending/Approved/Reimbursed
 *    KpiTile with coral / emerald / lime icon chips
 *  - DataTable rows with Avatar + employee name, mono money,
 *    StatusBadge (submitted/approved/reimbursed/rejected)
 * ───────────────────────────────────────────────────────────── */
const ExpensesMock: React.FC = () => (
  <MockShell title="worqly.io / dashboard / expenses">
    {/* KPI strip.
     *  NOTE the spans deliberately do NOT use `sm:`/`lg:` here. Tailwind
     *  breakpoints track the VIEWPORT, but this mock renders inside a ~580px
     *  column on a wide screen — so `sm:col-span-2` was handing each tile ~56px
     *  of usable width and the labels/icons burst straight out of their cards.
     *  Fixed spans keep the mock correct at every viewport. */}
    <div className="grid grid-cols-12 gap-3 mb-3">
      <div className="col-span-12 rounded-3xl bg-white/72 dark:bg-white/[0.05] backdrop-blur-xl shadow-card ring-1 ring-ink/[0.08] dark:ring-white/10 p-4">
        <div className="flex items-start justify-between mb-2">
          <Eyebrow className="min-w-0 truncate">Total in view</Eyebrow>
          <div className="h-9 w-9 rounded-full bg-coral/10 flex items-center justify-center shrink-0">
            <Receipt className="h-3.5 w-3.5 text-coral-600" />
          </div>
        </div>
        <p className="text-stat-md tabular-nums font-bold text-ink dark:text-white leading-none"><CountUp value="$8,142.30" /></p>
        <p className="text-[12px] text-ink/55 dark:text-white/55 mt-1.5">Across 38 expenses</p>
      </div>
      <div className="col-span-4 rounded-3xl bg-white/72 dark:bg-white/[0.05] backdrop-blur-xl shadow-card ring-1 ring-ink/[0.08] dark:ring-white/10 p-3">
        <div className="flex items-center justify-between mb-1.5">
          <Eyebrow className="!text-[10px] min-w-0 truncate">Pending</Eyebrow>
          <div className="h-7 w-7 rounded-full bg-coral/10 flex items-center justify-center shrink-0">
            <AlertCircle className="h-3 w-3 text-coral-600" />
          </div>
        </div>
        <p className="text-[15px] font-bold tabular-nums text-ink dark:text-white leading-none"><CountUp value="$1,420" /></p>
        <p className="text-[10px] text-ink/55 dark:text-white/55 mt-1">7 to review</p>
      </div>
      <div className="col-span-4 rounded-3xl bg-white/72 dark:bg-white/[0.05] backdrop-blur-xl shadow-card ring-1 ring-ink/[0.08] dark:ring-white/10 p-3">
        <div className="flex items-center justify-between mb-1.5">
          <Eyebrow className="!text-[10px] min-w-0 truncate">Approved</Eyebrow>
          <div className="h-7 w-7 rounded-full bg-emerald-500/10 flex items-center justify-center shrink-0">
            <CheckCircle2 className="h-3 w-3 text-emerald-600" />
          </div>
        </div>
        <p className="text-[15px] font-bold tabular-nums text-ink dark:text-white leading-none"><CountUp value="$3,840" /></p>
        <p className="text-[10px] text-ink/55 dark:text-white/55 mt-1">14 cleared</p>
      </div>
      <div className="col-span-4 rounded-3xl bg-white/72 dark:bg-white/[0.05] backdrop-blur-xl shadow-card ring-1 ring-ink/[0.08] dark:ring-white/10 p-3">
        <div className="flex items-center justify-between mb-1.5">
          <Eyebrow className="!text-[10px] min-w-0 truncate">Reimbursed</Eyebrow>
          <div className="h-7 w-7 rounded-full bg-lime-neon flex items-center justify-center shrink-0">
            <Wallet className="h-3 w-3 text-ink" />
          </div>
        </div>
        <p className="text-[15px] font-bold tabular-nums text-ink dark:text-white leading-none"><CountUp value="$2,882" /></p>
        <p className="text-[10px] text-ink/55 dark:text-white/55 mt-1">17 paid back</p>
      </div>
    </div>
    {/* Table — mirrors ExpensesTable */}
    <div className="rounded-3xl bg-white/72 dark:bg-white/[0.05] backdrop-blur-xl shadow-card ring-1 ring-ink/[0.08] dark:ring-white/10 overflow-hidden">
      <div className="grid grid-cols-12 gap-2 px-4 py-2.5 text-[10px] uppercase tracking-[0.14em] font-semibold text-ink/55 dark:text-white/55">
        {/* Status needs 3 cols: a "Reimbursed" pill is ~95px and a 1-col slot
         *  was ~78px, so the pills were being sliced by the card's overflow. */}
        <div className="col-span-2">Date</div>
        <div className="col-span-3">Employee</div>
        <div className="col-span-2">Category</div>
        <div className="col-span-2 text-right">Amount</div>
        <div className="col-span-3 text-right">Status</div>
      </div>
      <ul className="divide-y divide-ink/6 dark:divide-white/8">
        {[
          { date: 'Mar 14', name: 'Aria Patel',   cat: 'Software', amt: '$240.00', status: 'approved' as PillTone, label: 'Approved' },
          { date: 'Mar 12', name: 'Mason Cole',   cat: 'Travel',   amt: '$486.20', status: 'submitted' as PillTone, label: 'Submitted' },
          { date: 'Mar 09', name: 'Lina Ortega',  cat: 'Office',   amt: '$54.99',  status: 'reimbursed' as PillTone, label: 'Reimbursed' },
          { date: 'Mar 06', name: 'Jules Hartley',cat: 'Meals',    amt: '$82.40',  status: 'approved' as PillTone, label: 'Approved' },
          { date: 'Mar 02', name: 'Ravi Mendez',  cat: 'Travel',   amt: '$312.10', status: 'rejected' as PillTone, label: 'Rejected' },
        ].map((e) => (
          <li key={e.date + e.name} className="grid grid-cols-12 gap-2 px-4 py-3 items-center hover:bg-white/40 dark:hover:bg-white/[0.04] transition-colors">
            <div className="col-span-2 text-[12px] text-ink dark:text-white tabular-nums">{e.date}</div>
            <div className="col-span-3 flex items-center gap-2 min-w-0">
              <span
                aria-hidden
                className="h-6 w-6 rounded-full ring-1 ring-ink/5 dark:ring-white/10 flex items-center justify-center text-[9.5px] font-bold text-ink/85 shrink-0"
                style={{ background: avatarBg(e.name) }}
              >
                {initials(e.name)}
              </span>
              <span className="text-[12px] text-ink dark:text-white truncate">{e.name}</span>
            </div>
            <div className="col-span-2 text-[12px] text-ink/75 dark:text-white/75 truncate">{e.cat}</div>
            <div className="col-span-2 text-right text-[12.5px] font-semibold tabular-nums font-mono text-ink dark:text-white">{e.amt}</div>
            <div className="col-span-3 flex justify-end">
              <StatusPill tone={e.status}>{e.label}</StatusPill>
            </div>
          </li>
        ))}
      </ul>
    </div>
  </MockShell>
);

/* ─────────────────────────── HR / Team
 *
 * Mirrors frontend/src/modules/hr/pages/TeamPage.tsx:
 *  - 4-up KPI strip (Total / Active / Pending / Inactive) with
 *    rounded-lg icon chips (bg-success/10, bg-saffron/15, ...)
 *  - 2x2 grid of employee cards with name-hash pastel avatar tile,
 *    Dept + Hired meta, status pill (Active / Invite pending)
 * ───────────────────────────────────────────────────────────── */
const HRMock: React.FC = () => (
  <MockShell title="worqly.io / dashboard / hr / team">
    {/* KPI strip */}
    <div className="grid grid-cols-4 gap-2.5 mb-3">
      {[
        { label: 'Total',    value: 22, caption: 'On the team', icon: <Users className="h-3 w-3" />,     toneBg: 'bg-ink/5 dark:bg-white/10 text-ink/55 dark:text-white/65 ring-ink/10 dark:ring-white/15' },
        { label: 'Active',   value: 18, caption: 'Signed in',   icon: <UserCheck className="h-3 w-3" />, toneBg: 'bg-emerald-500/10 text-emerald-600 ring-emerald-500/25' },
        { label: 'Pending',  value: 3,  caption: 'Invite sent', icon: <Mailbox className="h-3 w-3" />,   toneBg: 'bg-coral/15 text-coral-700 ring-coral/30' },
        { label: 'Inactive', value: 1,  caption: 'Disabled',    icon: <Users className="h-3 w-3" />,     toneBg: 'bg-ink/5 dark:bg-white/10 text-ink/55 dark:text-white/65 ring-ink/10 dark:ring-white/15' },
      ].map((k) => (
        <div key={k.label} className="rounded-3xl bg-white/72 dark:bg-white/[0.05] backdrop-blur-xl shadow-card ring-1 ring-ink/[0.08] dark:ring-white/10 p-3">
          <div className="flex items-center justify-between mb-1.5">
            <Eyebrow className="!text-[10px]">{k.label}</Eyebrow>
            <span className={`inline-flex h-6 w-6 items-center justify-center rounded-lg ring-1 ${k.toneBg}`}>{k.icon}</span>
          </div>
          <p className="font-sans font-light text-[22px] leading-none tracking-[-0.03em] tabular-nums text-ink dark:text-white">{k.value}</p>
          <p className="text-[10px] text-ink/55 dark:text-white/55 mt-1.5">{k.caption}</p>
        </div>
      ))}
    </div>
    {/* Employee cards 2x2 */}
    <div className="grid grid-cols-2 gap-3">
      {[
        { name: 'Aria Patel',    email: 'aria@northwind.studio', dept: 'Studio', hired: 'Jan 12', state: 'active' as PillTone, label: 'Active' },
        { name: 'Mason Cole',    email: 'mason@northwind.studio', dept: 'Build',  hired: 'Feb 03', state: 'active' as PillTone, label: 'Active' },
        { name: 'Lina Ortega',   email: 'lina@northwind.studio',  dept: 'Ops',    hired: 'Feb 28', state: 'pending' as PillTone, label: 'Invite pending' },
        { name: 'Jules Hartley', email: 'jules@northwind.studio', dept: 'Design', hired: 'Mar 04', state: 'active' as PillTone, label: 'Active' },
      ].map((p) => (
        <div key={p.name} className="rounded-3xl bg-white/72 dark:bg-white/[0.05] backdrop-blur-xl shadow-card ring-1 ring-ink/[0.08] dark:ring-white/10 p-4">
          <div className="flex items-start gap-2.5">
            <span
              aria-hidden
              className="h-10 w-10 rounded-2xl ring-1 ring-ink/8 dark:ring-white/10 flex items-center justify-center text-[11px] font-bold text-ink/85 shrink-0"
              style={{ background: avatarBg(p.name) }}
            >
              {initials(p.name)}
            </span>
            <div className="flex-1 min-w-0">
              <p className="text-[13px] font-semibold text-ink dark:text-white truncate">{p.name}</p>
              <p className="text-[11px] text-ink/55 dark:text-white/55 truncate">{p.email}</p>
            </div>
          </div>
          <div className="mt-3 grid grid-cols-2 gap-2 text-[11px]">
            <div>
              <p className="text-[9.5px] font-semibold uppercase tracking-[0.12em] text-ink/55 dark:text-white/55">Dept</p>
              <p className="text-ink dark:text-white font-medium mt-0.5 truncate">{p.dept}</p>
            </div>
            <div>
              <p className="text-[9.5px] font-semibold uppercase tracking-[0.12em] text-ink/55 dark:text-white/55">Hired</p>
              <p className="text-ink dark:text-white font-medium mt-0.5 truncate">{p.hired}</p>
            </div>
          </div>
          <div className="mt-3 pt-3 border-t border-ink/6 dark:border-white/10">
            <StatusPill tone={p.state}>{p.label}</StatusPill>
          </div>
        </div>
      ))}
    </div>
  </MockShell>
);

/* ─────────────────────────── Payroll
 *
 * Mirrors frontend/src/modules/payroll/pages/PayrollPage.tsx:
 *  - 3-up Kpi strip (Total runs / Finalized / Drafts) with
 *    rounded-lg icon chips
 *  - List of runs (period date range + meta + status pill).
 *    Each row has a saffron-tinted Banknote icon tile.
 * ───────────────────────────────────────────────────────────── */
const PayrollMock: React.FC = () => (
  <MockShell title="worqly.io / dashboard / payroll">
    {/* KPI strip */}
    <div className="grid grid-cols-3 gap-3 mb-3">
      {[
        { label: 'Total runs', value: 14, caption: 'All-time',         icon: <Calendar className="h-3 w-3" />, toneBg: 'bg-ink/5 dark:bg-white/10 text-ink/55 dark:text-white/65 ring-ink/10 dark:ring-white/15' },
        { label: 'Finalized',  value: 12, caption: '11 distributed',   icon: <Banknote className="h-3 w-3" />, toneBg: 'bg-emerald-500/10 text-emerald-600 ring-emerald-500/25' },
        { label: 'Drafts',     value: 2,  caption: 'Open to edit',     icon: <FileDown className="h-3 w-3" />, toneBg: 'bg-coral/15 text-coral-700 ring-coral/30' },
      ].map((k) => (
        <div key={k.label} className="rounded-3xl bg-white/72 dark:bg-white/[0.05] backdrop-blur-xl shadow-card ring-1 ring-ink/[0.08] dark:ring-white/10 p-4">
          <div className="flex items-center justify-between mb-2">
            <Eyebrow>{k.label}</Eyebrow>
            <span className={`inline-flex h-7 w-7 items-center justify-center rounded-lg ring-1 ${k.toneBg}`}>{k.icon}</span>
          </div>
          <p className="font-sans font-light text-[26px] leading-none tracking-[-0.03em] tabular-nums text-ink dark:text-white">{k.value}</p>
          <p className="text-[11px] text-ink/55 dark:text-white/55 mt-1.5">{k.caption}</p>
        </div>
      ))}
    </div>
    {/* Runs list */}
    <div className="rounded-3xl bg-white/72 dark:bg-white/[0.05] backdrop-blur-xl shadow-card ring-1 ring-ink/[0.08] dark:ring-white/10 overflow-hidden">
      <ul className="divide-y divide-ink/6 dark:divide-white/10">
        {[
          { period: 'Apr 01 – Apr 30, 2026', meta: 'Finalized Apr 30 · Distributed May 02 · 22 payslips', total: '$48,210', status: 'success' as PillTone, label: 'Distributed' },
          { period: 'Mar 01 – Mar 31, 2026', meta: 'Finalized Mar 30 · Distributed Apr 02',                total: '$46,180', status: 'success' as PillTone, label: 'Distributed' },
          { period: 'Feb 01 – Feb 29, 2026', meta: 'Finalized Feb 28',                                     total: '$45,090', status: 'info' as PillTone,    label: 'Finalized' },
          { period: 'May 01 – May 31, 2026', meta: 'Open draft · Penalty rules applied',                   total: '$48,640', status: 'pending' as PillTone, label: 'Draft' },
        ].map((r) => (
          <li key={r.period} className="flex items-center gap-3 px-4 py-3 hover:bg-white/40 dark:hover:bg-white/[0.04] transition-colors">
            <div className="h-10 w-10 rounded-2xl bg-coral/15 ring-1 ring-coral/30 flex items-center justify-center shrink-0">
              <Banknote className="h-3.5 w-3.5 text-coral-700" strokeWidth={1.7} />
            </div>
            <div className="flex-1 min-w-0">
              <p className="text-[13px] font-semibold text-ink dark:text-white truncate">{r.period}</p>
              <p className="text-[11px] text-ink/55 dark:text-white/55 mt-0.5 truncate">{r.meta}</p>
            </div>
            <div className="flex flex-col items-end gap-1.5 shrink-0">
              <StatusPill tone={r.status}>{r.label}</StatusPill>
              <span className="text-[12px] font-bold tabular-nums text-ink dark:text-white leading-none">{r.total}</span>
            </div>
          </li>
        ))}
      </ul>
    </div>
  </MockShell>
);

/* ─────────────────────────── Projects
 *
 * Mirrors frontend/src/modules/projects/pages/ProjectsPage.tsx:
 *  - Active / Archived KPIs + a "Now tracking" TimerWidget row
 *    with saffron Play icon tile
 *  - Projects list with Briefcase tile + name + mono code + status pill
 * ───────────────────────────────────────────────────────────── */
const ProjectsMock: React.FC = () => (
  <MockShell title="worqly.io / dashboard / projects">
    {/* KPI + timer */}
    <div className="grid grid-cols-12 gap-3 mb-3">
      <div className="col-span-3 rounded-3xl bg-white/72 dark:bg-white/[0.05] backdrop-blur-xl shadow-card ring-1 ring-ink/[0.08] dark:ring-white/10 p-4">
        <div className="flex items-center justify-between mb-2">
          <Eyebrow className="!text-[10px] min-w-0 truncate">Active</Eyebrow>
          <span className="inline-flex h-6 w-6 items-center justify-center rounded-lg bg-emerald-500/10 text-emerald-600 ring-1 ring-emerald-500/25">
            <Briefcase className="h-3 w-3" />
          </span>
        </div>
        <p className="font-sans font-light text-[26px] leading-none tracking-[-0.03em] tabular-nums text-ink dark:text-white">8</p>
        <p className="text-[10px] text-ink/55 dark:text-white/55 mt-1.5">In progress</p>
      </div>
      <div className="col-span-3 rounded-3xl bg-white/72 dark:bg-white/[0.05] backdrop-blur-xl shadow-card ring-1 ring-ink/[0.08] dark:ring-white/10 p-4">
        <div className="flex items-center justify-between mb-2">
          <Eyebrow className="!text-[10px] min-w-0 truncate">Archived</Eyebrow>
          <span className="inline-flex h-6 w-6 items-center justify-center rounded-lg bg-ink/5 dark:bg-white/10 text-ink/55 dark:text-white/65 ring-1 ring-ink/10 dark:ring-white/15">
            <FolderKanban className="h-3 w-3" />
          </span>
        </div>
        <p className="font-sans font-light text-[26px] leading-none tracking-[-0.03em] tabular-nums text-ink dark:text-white">21</p>
        <p className="text-[10px] text-ink/55 dark:text-white/55 mt-1.5">Closed out</p>
      </div>
      <div className="col-span-6 rounded-3xl bg-white/72 dark:bg-white/[0.05] backdrop-blur-xl shadow-card ring-1 ring-ink/[0.08] dark:ring-white/10 p-4 flex items-center gap-3">
        <div className="h-9 w-9 rounded-2xl bg-coral/15 ring-1 ring-coral/30 flex items-center justify-center shrink-0">
          <Play className="h-3.5 w-3.5 text-coral-700 fill-current ml-0.5" />
        </div>
        <div className="flex-1 min-w-0">
          <Eyebrow className="!text-[10px] mb-1">Now tracking</Eyebrow>
          <div className="flex items-baseline gap-2">
            <span className="text-[20px] font-bold tabular-nums text-ink dark:text-white leading-none">02:35:18</span>
            <span className="text-[11px] text-ink/55 dark:text-white/55 truncate">Garden remodel · Aria</span>
          </div>
        </div>
      </div>
    </div>
    {/* Projects list */}
    <div className="rounded-3xl bg-white/72 dark:bg-white/[0.05] backdrop-blur-xl shadow-card ring-1 ring-ink/[0.08] dark:ring-white/10 overflow-hidden">
      <ul className="divide-y divide-ink/6 dark:divide-white/10">
        {[
          { name: 'Garden remodel',     code: 'PRJ-014', meta: 'Created Mar 04',  status: 'active' as PillTone,   label: 'Active' },
          { name: 'Verdant Lab rebrand', code: 'PRJ-013', meta: 'Created Feb 22', status: 'active' as PillTone,   label: 'Active' },
          { name: 'Harborline LLC site', code: 'PRJ-012', meta: 'Created Feb 14', status: 'active' as PillTone,   label: 'Active' },
          { name: 'Pinegate Q1 retainer',code: 'PRJ-011', meta: 'Closed Mar 31',  status: 'archived' as PillTone, label: 'Archived' },
        ].map((p) => (
          <li key={p.code} className="flex items-center gap-3 px-4 py-3 hover:bg-white/40 dark:hover:bg-white/[0.04] transition-colors">
            <div className="h-10 w-10 rounded-2xl bg-ink/5 dark:bg-white/10 ring-1 ring-ink/10 dark:ring-white/15 flex items-center justify-center shrink-0">
              <Briefcase className="h-3.5 w-3.5 text-ink/55 dark:text-white/65" strokeWidth={1.7} />
            </div>
            <div className="flex-1 min-w-0">
              <div className="flex items-baseline gap-2 flex-wrap">
                <span className="text-[13px] font-semibold text-ink dark:text-white truncate">{p.name}</span>
                <span className="text-[10.5px] font-mono text-ink/45 dark:text-white/45">{p.code}</span>
              </div>
              <p className="text-[11px] text-ink/55 dark:text-white/55 mt-0.5">{p.meta}</p>
            </div>
            <StatusPill tone={p.status}>{p.label}</StatusPill>
          </li>
        ))}
      </ul>
    </div>
  </MockShell>
);

/* ─────────────────────────── Monitoring
 *
 * Mirrors frontend/src/modules/monitoring/pages/MonitoringPage.tsx +
 * ScreenshotCard.tsx — employee selector chip, range buttons (Today /
 * Yesterday / Last 7 days), and a grid of screenshot cards with
 * a "captured_at" footer + status pills (Idle / Off hours).
 * ───────────────────────────────────────────────────────────── */
const MonitoringMock: React.FC = () => (
  <MockShell title="worqly.io / dashboard / monitoring">
    {/* Controls row */}
    <div className="flex flex-wrap items-center gap-2 mb-3">
      <span className="text-[11px] font-semibold text-ink/55 dark:text-white/55">Employee</span>
      <div className="inline-flex items-center gap-1.5 px-3 h-8 rounded-full bg-white/72 dark:bg-white/[0.05] ring-1 ring-ink/[0.08] dark:ring-white/10 shadow-pill text-[12px] font-semibold text-ink dark:text-white">
        <span
          aria-hidden
          className="h-4 w-4 rounded-full ring-1 ring-ink/10 dark:ring-white/15"
          style={{ background: avatarBg('Aria Patel') }}
        />
        Aria Patel
      </div>
      <div className="ml-auto inline-flex items-center gap-1 p-1 rounded-full bg-ink/5 dark:bg-white/10 ring-1 ring-ink/8 dark:ring-white/15">
        <button type="button" className="inline-flex items-center h-7 px-3 rounded-full bg-ink-900 text-white text-[11px] font-semibold shadow-cta-dark">Today</button>
        <button type="button" className="inline-flex items-center h-7 px-3 rounded-full text-[11px] font-semibold text-ink/65 dark:text-white/65">Yesterday</button>
        <button type="button" className="inline-flex items-center h-7 px-3 rounded-full text-[11px] font-semibold text-ink/65 dark:text-white/65">Last 7</button>
      </div>
    </div>
    {/* Screenshot grid */}
    <div className="grid grid-cols-3 gap-2.5 mb-3">
      {[
        { time: '10:42 AM', app: 'Screen 1 · captured',  bars: [60, 80, 95, 70] as number[], state: null as PillTone | null,    label: '',         tint: 'from-pearl-200 via-coral/30 to-coral-100 dark:from-white/[0.05] dark:via-white/[0.08] dark:to-white/[0.12]' },
        { time: '10:57 AM', app: 'Screen 1 · idle window',  bars: [45, 90, 65, 85] as number[], state: 'submitted' as PillTone, label: 'Idle',     tint: 'from-pearl-100 via-lavender-200 to-pearl-50 dark:from-white/[0.04] dark:via-white/[0.06] dark:to-white/[0.10]' },
        { time: '11:12 AM', app: 'Screen 2 · captured',      bars: [70, 50, 80, 60] as number[], state: null,                          label: '',         tint: 'from-pearl-100 via-pearl-200 to-coral/20 dark:from-white/[0.05] dark:via-white/[0.08] dark:to-white/[0.10]' },
      ].map((s) => (
        <div key={s.time} className="rounded-2xl bg-white/72 dark:bg-white/[0.05] backdrop-blur-xl shadow-card ring-1 ring-ink/[0.08] dark:ring-white/10 overflow-hidden">
          <div className={`aspect-video bg-gradient-to-br ${s.tint} flex items-end gap-1 p-2`}>
            {s.bars.map((h, i) => (
              <div key={i} className="flex-1 rounded-t-sm bg-ink/40 dark:bg-white/50" style={{ height: `${h}%` }} />
            ))}
          </div>
          <div className="px-2.5 py-1.5 flex items-center justify-between gap-1">
            <span className="text-[10px] text-ink/55 dark:text-white/55 tabular-nums">{s.time}</span>
            {s.state && <StatusPill tone={s.state}>{s.label}</StatusPill>}
          </div>
          <div className="px-2.5 pb-2 text-[10px] text-ink/55 dark:text-white/55 truncate">{s.app}</div>
        </div>
      ))}
    </div>
    {/* Weekly activity bars — secondary "This week" summary */}
    <div className="rounded-3xl bg-white/72 dark:bg-white/[0.05] backdrop-blur-xl shadow-card ring-1 ring-ink/[0.08] dark:ring-white/10 p-4">
      <div className="flex items-center justify-between mb-2">
        <Eyebrow className="min-w-0 truncate">Activity · last 7 days</Eyebrow>
        <span className="text-[11px] text-ink/55 dark:text-white/55 tabular-nums">38h 22m</span>
      </div>
      <div className="flex items-end gap-1.5 h-10">
        {[28, 42, 36, 56, 48, 64, 58].map((h, i) => (
          <div
            key={i}
            className={`flex-1 rounded-t-md ${i === 5 ? 'bg-coral' : 'bg-coral/45 dark:bg-coral/55'}`}
            style={{ height: `${h}%` }}
          />
        ))}
      </div>
      <div className="mt-2 flex items-center justify-between text-[10px] text-ink/55 dark:text-white/55">
        <span>Mon</span><span>Tue</span><span>Wed</span><span>Thu</span><span>Fri</span><span className="text-ink dark:text-white font-semibold">Sat</span><span>Sun</span>
      </div>
    </div>
  </MockShell>
);

/* ─────────────────────────── Payments
 *
 * Mirrors frontend/src/modules/payments/pages/PaymentsPage.tsx +
 * HistoryTab.tsx — a wide "Total collected" GlassCard with a
 * WaveformBars chart and a lime "· Live · Stripe connected" pill,
 * plus a DataTable below with When / Amount / Method / Customer /
 * Invoice / Status columns.
 * ───────────────────────────────────────────────────────────── */
const PaymentsMock: React.FC = () => (
  <MockShell title="worqly.io / dashboard / payments">
    {/* Big total + waveform */}
    <div className="rounded-3xl bg-white/72 dark:bg-white/[0.05] backdrop-blur-xl shadow-card ring-1 ring-ink/[0.08] dark:ring-white/10 p-5 mb-3">
      <div className="flex items-end justify-between mb-4">
        <div>
          <Eyebrow className="mb-2">Total collected</Eyebrow>
          <p className="text-stat-md tabular-nums font-bold text-ink dark:text-white leading-none"><CountUp value="$74,420.00" /></p>
          <p className="text-[12px] text-ink/55 dark:text-white/55 mt-1.5">$24,180 in the last 30 days · 142 transactions</p>
        </div>
        <span className="inline-flex items-center gap-1.5 px-2.5 py-1 bg-lime-neon text-ink rounded-full text-[10px] font-bold uppercase tracking-[0.10em]">
          · Live · Stripe connected
        </span>
      </div>
      {/* WaveformBars approximation — center-anchored bars */}
      <div className="flex items-center gap-1 h-12">
        {[40, 60, 30, 80, 50, 90, 45, 70, 60, 95, 55, 75, 65, 85, 50, 70, 80, 60, 90, 55, 75, 65].map((h, i) => (
          <div key={i} className="flex-1 flex flex-col items-center justify-center gap-px">
            <div className={`w-full rounded-full ${i % 3 === 0 ? 'bg-coral' : 'bg-coral/45 dark:bg-coral/55'}`} style={{ height: `${h * 0.5}%` }} />
            <div className={`w-full rounded-full ${i % 3 === 0 ? 'bg-coral/60' : 'bg-coral/25 dark:bg-coral/35'}`} style={{ height: `${h * 0.35}%` }} />
          </div>
        ))}
      </div>
    </div>
    {/* History table */}
    <div className="rounded-3xl bg-white/72 dark:bg-white/[0.05] backdrop-blur-xl shadow-card ring-1 ring-ink/[0.08] dark:ring-white/10 overflow-hidden">
      <div className="grid grid-cols-12 gap-2 px-4 py-2.5 text-[10px] uppercase tracking-[0.14em] font-semibold text-ink/55 dark:text-white/55">
        <div className="col-span-3">When</div>
        <div className="col-span-2 text-right">Amount</div>
        <div className="col-span-2">Method</div>
        <div className="col-span-2">Customer</div>
        <div className="col-span-3 text-right">Status</div>
      </div>
      <ul className="divide-y divide-ink/6 dark:divide-white/10">
        {[
          { when: 'Apr 18 · 10:42', amt: '$2,840.00', method: 'Card',     who: 'Pinegate Co.',     status: 'success' as PillTone, label: 'Succeeded' },
          { when: 'Apr 17 · 16:18', amt: '$1,560.00', method: 'Bank transfer', who: 'Salt River Inc.',  status: 'success' as PillTone, label: 'Succeeded' },
          { when: 'Apr 15 · 09:04', amt: '$4,820.00', method: 'Card',     who: 'Northwind Studio', status: 'success' as PillTone, label: 'Succeeded' },
          { when: 'Apr 12 · 14:30', amt: '$960.00',   method: 'Cheque',   who: 'Rivermark Agency', status: 'pending' as PillTone, label: 'Refunded' },
        ].map((r) => (
          <li key={r.when} className="grid grid-cols-12 gap-2 px-4 py-2.5 items-center hover:bg-white/40 dark:hover:bg-white/[0.04] transition-colors">
            <div className="col-span-3 text-[12px] text-ink/55 dark:text-white/55 tabular-nums">{r.when}</div>
            <div className="col-span-2 text-right text-[12.5px] font-semibold tabular-nums font-mono text-ink dark:text-white">{r.amt}</div>
            <div className="col-span-2 text-[12px] text-ink/75 dark:text-white/75 truncate">{r.method}</div>
            <div className="col-span-2 text-[12px] text-ink dark:text-white truncate">{r.who}</div>
            <div className="col-span-3 flex justify-end">
              <StatusPill tone={r.status}>{r.label}</StatusPill>
            </div>
          </li>
        ))}
      </ul>
    </div>
  </MockShell>
);

/* ─────────────────────────── Email
 *
 * Mirrors frontend/src/modules/email/pages/EmailSettingsPage.tsx +
 * LogTab.tsx — a tab strip (Provider / Templates / Test / Log) and
 * a delivery log table with mono template_key, To, Subject, and a
 * status pill (Sent / Failed / Pending).
 * ───────────────────────────────────────────────────────────── */
const EmailMock: React.FC = () => (
  <MockShell title="worqly.io / dashboard / email">
    {/* Tabs */}
    <div className="inline-flex items-center gap-1 p-1 rounded-full bg-ink/5 dark:bg-white/10 ring-1 ring-ink/8 dark:ring-white/15 mb-3">
      {[
        { k: 'provider',  label: 'Provider', active: false },
        { k: 'templates', label: 'Templates', active: false },
        { k: 'test',      label: 'Test send', active: false },
        { k: 'log',       label: 'Log',       active: true },
      ].map((t) => (
        <button
          key={t.k}
          type="button"
          className={
            'inline-flex items-center h-7 px-3.5 rounded-full text-[11.5px] font-semibold transition-colors ' +
            (t.active
              ? 'bg-ink-900 text-white shadow-cta-dark dark:bg-white dark:text-ink'
              : 'text-ink/65 dark:text-white/65')
          }
        >
          {t.label}
        </button>
      ))}
    </div>
    {/* Deliverability KPI row */}
    <div className="grid grid-cols-3 gap-2.5 mb-3">
      {[
        { l: 'Sent · 7d', v: '412',  c: 'Delivered' },
        { l: 'Open rate', v: '68%',  c: '+ 4.2% vs last week' },
        { l: 'Bounce',    v: '0.4%', c: '2 bounced · 0 spam' },
      ].map((k) => (
        <div key={k.l} className="rounded-3xl bg-white/72 dark:bg-white/[0.05] backdrop-blur-xl shadow-card ring-1 ring-ink/[0.08] dark:ring-white/10 p-3.5">
          <Eyebrow className="!text-[10px]">{k.l}</Eyebrow>
          <p className="mt-1.5 text-[18px] font-bold tabular-nums text-ink dark:text-white leading-none">{k.v}</p>
          <p className="text-[10px] text-ink/55 dark:text-white/55 mt-1.5">{k.c}</p>
        </div>
      ))}
    </div>
    {/* Log table */}
    <div className="rounded-3xl bg-white/72 dark:bg-white/[0.05] backdrop-blur-xl shadow-card ring-1 ring-ink/[0.08] dark:ring-white/10 overflow-hidden">
      <div className="grid grid-cols-12 gap-2 px-4 py-2.5 text-[10px] uppercase tracking-[0.14em] font-semibold text-ink/55 dark:text-white/55">
        <div className="col-span-3">When</div>
        <div className="col-span-3">Template</div>
        <div className="col-span-4">To</div>
        <div className="col-span-2 text-right">Status</div>
      </div>
      <ul className="divide-y divide-ink/6 dark:divide-white/10">
        {[
          { when: 'Apr 18 · 10:42', tpl: 'INVOICE_PAID',     to: 'billing@pinegate.example',  status: 'success' as PillTone,  label: 'Sent' },
          { when: 'Apr 18 · 09:14', tpl: 'INVOICE_REMINDER', to: 'ar@saltriver.example',      status: 'success' as PillTone,  label: 'Sent' },
          { when: 'Apr 17 · 17:30', tpl: 'PAYSLIP',          to: '22 employees · batch',      status: 'success' as PillTone,  label: 'Sent' },
          { when: 'Apr 17 · 14:02', tpl: 'INVOICE_SENT',     to: 'ops@northwind.example',     status: 'info' as PillTone,     label: 'Pending' },
          { when: 'Apr 16 · 11:18', tpl: 'INVOICE_OVERDUE',  to: 'unknown@rivermark.example', status: 'overdue' as PillTone,  label: 'Failed' },
        ].map((m) => (
          <li key={m.when} className="grid grid-cols-12 gap-2 px-4 py-2.5 items-center hover:bg-white/40 dark:hover:bg-white/[0.04] transition-colors">
            <div className="col-span-3 text-[12px] text-ink/55 dark:text-white/55 tabular-nums">{m.when}</div>
            <div className="col-span-3 text-[10.5px] font-mono uppercase tracking-wide text-ink/85 dark:text-white/85 truncate">{m.tpl}</div>
            <div className="col-span-4 text-[12px] text-ink dark:text-white truncate">{m.to}</div>
            <div className="col-span-2 text-right">
              <StatusPill tone={m.status}>{m.label}</StatusPill>
            </div>
          </li>
        ))}
      </ul>
    </div>
  </MockShell>
);

const EsignMock: React.FC = () => (
  <MockShell title="worqly.io / dashboard / esign">
    {/* KPI row */}
    <div className="grid grid-cols-3 gap-2.5 mb-3">
      {[
        { l: 'Sent · 30d', v: '18',  c: '12 completed' },
        { l: 'Avg. turnaround', v: '4.2h',  c: 'first sign to last' },
        { l: 'Completion rate', v: '92%', c: '1 declined' },
      ].map((k) => (
        <div key={k.l} className="rounded-3xl bg-white/72 dark:bg-white/[0.05] backdrop-blur-xl shadow-card ring-1 ring-ink/[0.08] dark:ring-white/10 p-3.5">
          <Eyebrow className="!text-[10px]">{k.l}</Eyebrow>
          <p className="mt-1.5 text-[18px] font-bold tabular-nums text-ink dark:text-white leading-none">{k.v}</p>
          <p className="text-[10px] text-ink/55 dark:text-white/55 mt-1.5">{k.c}</p>
        </div>
      ))}
    </div>
    {/* Documents table */}
    <div className="rounded-3xl bg-white/72 dark:bg-white/[0.05] backdrop-blur-xl shadow-card ring-1 ring-ink/[0.08] dark:ring-white/10 overflow-hidden">
      <div className="grid grid-cols-12 gap-2 px-4 py-2.5 text-[10px] uppercase tracking-[0.14em] font-semibold text-ink/55 dark:text-white/55">
        <div className="col-span-5">Document</div>
        <div className="col-span-4">Signers</div>
        <div className="col-span-3 text-right">Status</div>
      </div>
      <ul className="divide-y divide-ink/6 dark:divide-white/10">
        {[
          { doc: 'MSA — Northwind Co',      signers: '2 of 2 signed', status: 'success' as PillTone, label: 'Completed' },
          { doc: 'NDA — Rivermark LLC',     signers: '1 of 2 signed', status: 'info' as PillTone,     label: 'Sent' },
          { doc: 'SOW — Saltriver Studio',  signers: '0 of 3 signed', status: 'draft' as PillTone,    label: 'Draft' },
          { doc: 'Vendor agreement',        signers: '1 of 1 signed', status: 'overdue' as PillTone,  label: 'Declined' },
        ].map((m) => (
          <li key={m.doc} className="grid grid-cols-12 gap-2 px-4 py-2.5 items-center hover:bg-white/40 dark:hover:bg-white/[0.04] transition-colors">
            <div className="col-span-5 text-[12px] text-ink dark:text-white truncate">{m.doc}</div>
            <div className="col-span-4 text-[11.5px] text-ink/65 dark:text-white/65 truncate">{m.signers}</div>
            <div className="col-span-3 flex justify-end">
              <StatusPill tone={m.status}>{m.label}</StatusPill>
            </div>
          </li>
        ))}
      </ul>
    </div>
  </MockShell>
);

/* ─────────────────────────────────────────────────────────────
 * Module data — anchor IDs match landing page links.
 * ───────────────────────────────────────────────────────────── */

const MODULES: Module[] = [
  {
    id: 'invoicing',
    eyebrow: 'Invoicing',
    Icon: FileText,
    Mock: InvoicingMock,
    title: (
      <>
        Send invoices customers <span className="italic font-serif text-coral">actually pay.</span>
      </>
    ),
    body: [
      'Branded PDFs, hosted Stripe pay links, and automated reminders — built so the invoice your customer opens looks like your brand, not a SaaS template.',
      'Recurring billing handles retainers and subscriptions on autopilot. Line items reuse from your service catalogue. Multi-currency, tax-aware, and pro-rated to the cycle.',
    ],
    bullets: [
      'Branded PDFs with your logo + colors',
      'Stripe-hosted card pay links',
      'Recurring billing, auto-pro-rated',
      'Automatic 3-day and 7-day reminders',
      'Multi-currency + tax tables',
      'Real-time status: draft → sent → paid',
    ],
  },
  {
    id: 'expenses',
    eyebrow: 'Expenses',
    Icon: Receipt,
    Mock: ExpensesMock,
    title: (
      <>
        Receipts in. Reports out. <span className="italic font-serif text-coral">No shoebox.</span>
      </>
    ),
    body: [
      'Snap or upload a receipt, route it through approval, and roll it into category reports that pair with the rest of your books.',
      'Approved expenses push straight into a payroll run as reimbursements — so the receipt your designer snapped at the supply store gets paid back on the next run, with an audit trail behind it.',
    ],
    bullets: [
      'Attach receipt photos or files to any expense',
      'Multi-step approvals with audit trail',
      'Your own expense categories + policies',
      'Reimbursement payouts on next payroll',
      'Category + spend reports in Reports Center',
      'CSV export for your accountant',
    ],
  },
  {
    id: 'hr',
    eyebrow: 'HR',
    Icon: Users,
    Mock: HRMock,
    title: (
      <>
        Your people, your departments, <span className="italic font-serif text-coral">one source of truth.</span>
      </>
    ),
    body: [
      'Employees, contractors, departments, attendance, leave — built from the same data model that drives payroll and project margin, so the org chart is never out of date.',
      'Magic-link onboarding gets a new hire from offer-letter to first timesheet without a single password reset ticket.',
    ],
    bullets: [
      'Employee + contractor records',
      'Departments, roles, reporting lines',
      'Attendance + leave tracking',
      'Magic-link onboarding (no password setup)',
      'Penalty rules that flow into payroll',
      'Bank details stored masked, used only by payroll',
    ],
  },
  {
    id: 'payroll',
    eyebrow: 'Payroll',
    Icon: Banknote,
    Mock: PayrollMock,
    title: (
      <>
        Payroll that <span className="italic font-serif text-coral">respects attendance.</span>
      </>
    ),
    body: [
      'Runs that read attendance and leave automatically — late marks, half-days, and approved leave all factor into gross pay without you reaching for a calculator.',
      'Generate payslips, push them to email, and export a bank-ready NACHA file in one click. Penalty rules and reimbursements settle on the same run.',
    ],
    bullets: [
      'Attendance-aware gross pay',
      'Configurable penalty rules',
      'Payslip PDFs (branded, emailed)',
      'NACHA (US ACH) bank export file',
      'Expense reimbursements bundled',
      'Per-employee history + audit log',
    ],
  },
  {
    id: 'projects',
    eyebrow: 'Projects',
    Icon: Briefcase,
    Mock: ProjectsMock,
    title: (
      <>
        See the <span className="italic font-serif text-coral">margin</span> on every job.
      </>
    ),
    body: [
      'Tasks, hours, and budget rolled into per-project margin — so when a customer asks "how are we doing on the Garden remodel?" the answer is one click, not a spreadsheet weekend.',
      'Time logged against a task is approved once and then turns straight into invoice lines — billable hours never get re-typed into a second system.',
    ],
    bullets: [
      'Tasks, milestones, owners',
      'Timesheet approval → invoice from time',
      'Per-project P&L with margin',
      'Budget vs actuals, live',
      'Milestone tracking against the budget',
      'Reports across projects + customers',
    ],
  },
  {
    id: 'monitoring',
    eyebrow: 'Monitoring',
    Icon: Activity,
    Mock: MonitoringMock,
    title: (
      <>
        Time tracking for teams that <span className="italic font-serif text-coral">opt in.</span>
      </>
    ),
    body: [
      'Per-project timers in the browser for everyone, plus an opt-in desktop agent for teams that want screenshots and activity at the keyboard. Every employee sees and approves what is captured.',
      'The desktop agent ships for Windows and macOS, and it captures the screen — not your apps, not your browser history. Workers can pause anywhere and the dashboard logs the gap honestly.',
    ],
    bullets: [
      'Browser timers — start / stop / switch',
      'Opt-in Windows / macOS agent (no mobile)',
      'Screen captures + activity, employee-visible',
      'No app, URL, or keystroke tracking',
      'Pause + resume with audit trail',
      'Agent clock-in/out feeds HR attendance → payroll',
    ],
  },
  {
    id: 'esign',
    eyebrow: 'E-Sign',
    Icon: PenTool,
    Mock: EsignMock,
    title: (
      <>
        Get it <span className="italic font-serif text-coral">signed.</span> Officially.
      </>
    ),
    body: [
      'Upload a PDF, drop signature fields on the pages that need them, and send. Every signer gets a tracked link, and the document seals itself the moment the last signature lands.',
      'Every completed document gets an official Worqly signature number that anyone can verify publicly — proof the document is authentic and untampered, without asking us for anything.',
    ],
    bullets: [
      'Upload any PDF, place fields visually',
      'Sequential or parallel signing order',
      'Tracked sign links, no account needed for signers',
      'Auto-sealed certificate page on completion',
      'Official signature number + public verification',
      'Full audit trail: viewed, signed, declined, voided',
    ],
  },
  {
    id: 'payments',
    eyebrow: 'Payments',
    Icon: CreditCard,
    Mock: PaymentsMock,
    title: (
      <>
        Stripe-powered. <span className="italic font-serif text-coral">Pass-through fees.</span>
      </>
    ),
    body: [
      'You connect your own Stripe account with your API keys — your customers pay your invoice through Stripe Checkout, the money lands in your Stripe balance and pays out to your bank on your Stripe schedule, and Worqly reconciles the payment back to the invoice automatically.',
      'Manual payments (cheque, bank transfer, cash) reconcile in one click. Stripe processing fees pass through with no markup — we never charge a payments fee on top.',
    ],
    bullets: [
      'Bring your own Stripe keys — your account, your payouts',
      'Stripe Checkout card payments',
      'Auto-reconciliation against invoices',
      'Manual payment recording',
      'No markup over Stripe fees',
      'PCI-safe — no card data on our servers',
    ],
  },
  {
    id: 'email',
    eyebrow: 'Email',
    Icon: Mail,
    Mock: EmailMock,
    title: (
      <>
        Transactional email <span className="italic font-serif text-coral">bundled in.</span>
      </>
    ),
    body: [
      'Every invoice, payslip, reminder, and notification ships from Worqly\'s transactional email infrastructure — no SMTP credentials to wire up, no SendGrid bill to manage.',
      'Customers see open + click + bounce stats on every outbound, with a per-message audit log. Custom-domain sending is available when you\'re ready to point DNS.',
    ],
    bullets: [
      'Invoices + payslips + reminders, all sent',
      'Branded sender (from name + reply-to)',
      'Open + click + bounce analytics',
      'Per-message delivery log',
      'Custom-domain sending (DKIM + SPF)',
      'Free — bundled with every workspace',
    ],
  },
];

/* ─────────────────────────────────────────────────────────────
 * FOOTER CTA — coral gradient, mirrors LandingPage + PricingPage.
 * ───────────────────────────────────────────────────────────── */

function FooterCTA() {
  const [email, setEmail] = React.useState('');
  const [submitting, setSubmitting] = React.useState(false);

  function onSubmit(e: React.FormEvent<HTMLFormElement>) {
    e.preventDefault();
    if (submitting) return;
    setSubmitting(true);
    const params = new URLSearchParams();
    if (email.trim()) params.set('email', email.trim());
    const qs = params.toString();
    window.location.href = qs ? `/signup?${qs}` : '/signup';
  }

  return (
    <section className="py-16 lg:py-20">
      <div className="max-w-site mx-auto px-6 lg:px-8">
        <div className="relative rounded-[32px] overflow-hidden bg-coral-gradient text-white p-10 lg:p-16 shadow-card-lg">
          <div aria-hidden className="absolute inset-0 pds-dotted opacity-15 mix-blend-overlay" />
          <div className="relative grid grid-cols-1 lg:grid-cols-12 gap-8 lg:gap-12 items-center">
            <div className="lg:col-span-7">
              <p className="text-[11px] font-bold uppercase tracking-[0.18em] text-white/80">
                Start free
              </p>
              <h2 className="mt-3 font-light text-display-md leading-[1.05] tracking-[-0.025em] text-balance">
                Eight modules. Seven free days. <em className="font-serif">Zero cards.</em>
              </h2>
              <p className="mt-4 text-body-lg text-white/85 max-w-xl">
                Turn on what you need today, add more when you grow. We never charge for seats
                that aren't using the module.
              </p>
            </div>
            <div className="lg:col-span-5">
              <form
                onSubmit={onSubmit}
                className="rounded-2xl bg-white/15 backdrop-blur-md ring-1 ring-white/25 p-2 flex flex-col sm:flex-row sm:items-center gap-2"
              >
                <label htmlFor="features-cta-email" className="sr-only">
                  Work email
                </label>
                <input
                  id="features-cta-email"
                  type="email"
                  required
                  value={email}
                  onChange={(e) => setEmail(e.target.value)}
                  placeholder="you@company.com"
                  className="flex-1 h-11 px-4 rounded-full bg-white text-ink text-[14px] placeholder:text-ink/40 outline-none focus:ring-2 focus:ring-white/60 dark:bg-white/10 dark:text-white dark:placeholder:text-white/40 dark:focus:ring-white/30"
                />
                <button
                  type="submit"
                  disabled={submitting}
                  className="inline-flex shrink-0 w-full sm:w-auto items-center justify-center gap-1.5 h-11 px-5 rounded-full bg-ink-900 text-white text-[13.5px] font-semibold hover:bg-black transition-colors disabled:opacity-60"
                >
                  Start free
                  <ArrowRight className="h-4 w-4" />
                </button>
              </form>
              <p className="mt-3 text-[12px] text-white/75 text-center sm:text-left">
                No card required. Add billing before day 8 to keep going.
              </p>
            </div>
          </div>
        </div>
      </div>
    </section>
  );
}
